import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import {
  AccessPermissionService,
  dblclickTooltipDef,
  defaultColumn,
  DELETE_RECORD_LABEL,
  grid_widths_map,
  mainModuleName,
  rolesModuleName,
} from '@piqet-angular/shared';
import { right } from '@popperjs/core';
import { CustomFacade } from '@piqet-angular/data';
import { cloneDeep } from 'lodash';
import { NumericCellEditor } from 'libs/ui/src/lib/numeri-cell-editor/numeric-cell-editor.component';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { getRolesAndPermission } from 'libs/shared/src/lib/service/helper.service';
import { Subject, take } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';

@Injectable({
  providedIn: 'root',
})
export class CustomStorageDataGridService {
  customStoragePayload: any;
  event: any;
  Access: any;

  editStorageSub = new Subject<[any, any, any, any]>();
  customStorageObservable$ = this.editStorageSub.asObservable();

  deleteStorageSub = new Subject<[any, any, any]>();
  deleteCustomStorageObservable$ = this.deleteStorageSub.asObservable();

  copyStorageSub = new Subject<[any, any, any]>();
  copyCustomStorageObservable$ = this.copyStorageSub.asObservable();

  inlineEditing = new Subject<[any, any]>();
  inlineEditingObservable$ = this.inlineEditing.asObservable();
  currentRoute: string = '';

  constructor(
    private CustomFacade: CustomFacade,
    private SharedService: SharedService,
    private accessService: AccessPermissionService,
    private swalService: SwalService
  ) {}

  accessAllPermission(type: string) {
    const access =
      this.isAccess(type, 'edit') || this.isAccess(type, 'delete') || this.isAccess(type, 'create');
    return access;
  }

  getCustomStorageDataColumn(pathName?: any): ColDef[] {
    this.currentRoute = pathName;
    const colDef: ColDef[] = [];
    colDef.push({
      ...defaultColumn,
      colId: 'ProcessName',
      headerName: 'My custom storage',
      field: 'processName',
      cellRenderer: (params: any) => {
        const cellValue = params.value;
        const cellElement = document.createElement('span');
        cellElement.style.cursor = 'pointer';
        cellElement.innerHTML = `${cellValue}`;
        const checkAccess = this.isAccess(pathName, 'edit') || this.isAccess(pathName, 'view');
        if (checkAccess) {
          cellElement.title = dblclickTooltipDef;
          cellElement.style.textDecoration = 'underline';
        }
        cellElement.addEventListener('dblclick', () => {
          checkAccess &&
            this.editStorageSub.next([
              params.data,
              params.data.id,
              'Review custom storage',
              pathName,
            ]);
        });
        return cellElement;
      },
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      editable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Cost per package (USD)',
      headerName: 'Cost per package (USD)',
      field: 'packageCostSum',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agNumberColumnFilter',
      editable: false,
    });

    {
      pathName == 'adminCustomStorage' &&
        colDef.push({
          ...defaultColumn,
          colId: 'OrganizationName',
          headerName: 'Organization name',
          field: 'organizationName',
          cellClass: 'my-permission-cursor-pointer',
          wrapText: true,
          autoHeight: true,
          lockVisible: true,
          resizable: false,
          editable: false,
          filter: 'agTextColumnFilter',
        });
    }

    colDef.push({
      ...defaultColumn,
      colId: 'OwnerName',
      headerName: 'Owner',
      field: 'ownerName',
      cellClass: 'my-permission-cursor-pointer',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      resizable: false,
      filter: 'agTextColumnFilter',
      editable: false,
    });

    this.accessAllPermission(pathName) &&
      colDef.push({
        ...defaultColumn,
        colId: 'Actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        cellRendererParams: {
          showEdit: this.isAccess(pathName, 'edit'),
          showCopy: this.isAccess(pathName, 'create'),
          showDelete: this.isAccess(pathName, 'delete'),
          value: 'customStorage',
          deleteData: (data: any, pathName: any) => {
            if (pathName == 'adminCustomStorage') {
              this.deleteCustomStorageConfirmation(
                data,
                '/AdminCustomStorage/DeleteCustomStorage',
                pathName
              );
            } else {
              this.deleteCustomStorageConfirmation(
                data,
                '/CustomStorage/DeleteCustomStorage',
                pathName
              );
            }
          },
          copyData: (data: any, pathName: any) => {
            if (pathName == 'adminCustomStorage') {
              this.copyStorageSub.next([
                data,
                '/AdminCustomStorage/SaveCopyCustomStorage',
                pathName,
              ]);
            } else {
              this.copyStorageSub.next([data, '/CustomStorage/SaveCopyCustomStorage', pathName]);
            }
          },
          editData: (data: any, pathName: any) => {
            this.editStorageSub.next([data, data.id, 'Review custom storage', pathName]);
          },

          copyDataEditRendarar: (data: any) => {
            return false;
          },
        },
        maxWidth: grid_widths_map.sm_column_xs,
        cellClass: 'my-permission-cursor-pointer',
        cellRenderer: 'actionRenderer',
        lockVisible: true,
        lockPosition: right,
        resizable: false,
      });

    return colDef;
  }

  isAccess = (pathName: any, type: 'view' | 'edit' | 'create' | 'delete') => {
    return pathName.includes('admin')
      ? this.accessService.hasModulePermission(
          rolesModuleName.AdministratorStorage,
          mainModuleName.projectManagementModule,
          type
        )
      : this.accessService.hasModulePermission(
          rolesModuleName.customStorage,
          mainModuleName.customModule,
          type
        );
  };

  deleteCustomStorageConfirmation(data: any, customType: any, pathName: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.deleteStorageSub.next([data, customType, pathName]);
      }
    });
  }

  getPayload(searchData: any, sortColumnState?: any, event?: any) {
    if (event) {
      this.event = event;
    }
    const currentPage = this.event?.goToPage
      ? this.event.goToPage
      : this.event?.next
      ? this.event.currentPage + 1
      : this.event?.prev
      ? this.event?.currentPage - 1
      : this.event?.gridApi?.paginationGetCurrentPage() + 1;
    const pageSize = this.event?.gridApi.paginationGetPageSize();
    const startCount = pageSize * (currentPage - 1);

    const columns = this.getCustomStorageDataColumn()
      .map((item: any) => {
        return {
          searchable: true,
          name: item.colId,
          orderable: true,
          data: item.colId,
        };
      })
      .filter(value => value?.name != 'Actions');

    return (this.customStoragePayload = {
      search: {
        columnNameList: [],
        value: searchData || '',
      },
      columns: columns,
      order: [
        {
          columnName: sortColumnState?.columnName || 'processName',
          dir:
            sortColumnState?.dir[0].toUpperCase() + sortColumnState?.dir.substr(1).toLowerCase() ||
            'Desc',
        },
      ],
      start: startCount || 0,
      length: pageSize == Number(this.event?.totalRecord) ? Number('-1') : pageSize || 10,
      draw: currentPage || 1,
    });
  }

  innedGridActionAccess(fromEdit: boolean): boolean {
    return (
      (!fromEdit && this.isAccess(this.currentRoute, 'create')) ||
      (fromEdit && !this.isAccess(this.currentRoute, 'view')) ||
      (fromEdit && this.isAccess(this.currentRoute, 'edit'))
    );
  }

  getNewProcessColumns(fromEdit: boolean = false): ColDef[] {
    let enableEditing: any;
    let resetData: any;
    let newValue: any;
    let energyProcessId: any;
    const colDef: ColDef[] = [];

    colDef.push({
      ...defaultColumn,
      colId: 'EnergyName',
      headerName: ' Process favourite for storage',
      field: 'energyName',
      cellEditor: 'GridCellRendererComponent',
      cellEditorParams: {
        from: 'storage',
        editData: (data: any) => {
          newValue = data.energyName;
          energyProcessId = data.energyProcessId;
        },
      },
      cellRenderer: (params: any) => {
        return `<div class="truncated-text" title="${params.value}">${
          params.value ? params.value : ''
        }</div>`;
      },
      valueSetter: (params: any) => {
        const newVal = !!newValue ? newValue : 'Select process';
        const valueChanged =
          !!newValue && !resetData ? params.data[params.colDef.field] !== newVal : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data[params.colDef.field] =
            typeof params.oldValue == 'number' ? parseFloat(newVal) : newVal;
          params.data['energyProcessId'] = energyProcessId;
          params.data['enableEditing'] = enableEditing;
          const colId = params.colDef.field;
          const nodeId = params.data.id;
          this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
            flag &&
              this.CustomFacade.updateStorageProcessActions(
                params.data,
                'edit',
                null,
                nodeId,
                colId
              );
          });

          newValue = '';
          energyProcessId = '';
        }
        return valueChanged;
      },
      valueGetter: params => {
        return params.data.energyName;
      },

      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      lockPosition: true,
      wrapHeaderText: true,
      resizable: false,
      editable: true,
      sortable: false,
    });

    colDef.push({
      ...defaultColumn,
      colId: 'Amount',
      headerName: 'Amount *',
      field: 'amount',
      cellRendererParams: {
        edit: 'amount',
      },
      // suppressColumnsToolPanel: true,
      // cellClass: 'my-permission-cursor-pointer',
      cellClass: 'no-focus-outline',
      wrapText: true,
      autoHeight: true,
      lockVisible: true,
      wrapHeaderText: true,
      resizable: false,
      editable: true,
      sortable: false,
      cellEditor: NumericCellEditor,
      valueSetter: (params: any) => {
        const newValInt = params.newValue;
        const valueChanged = !resetData ? params.data.amount !== newValInt : false;
        params.data = { ...params.data };
        if (valueChanged) {
          params.data.amount =
            typeof params.oldValue == 'number' ? parseFloat(newValInt) : newValInt;
        }
        params.data['enableEditing'] = enableEditing;
        const colId = params.colDef.field;
        const nodeId = params.data.id;
        this.CustomFacade.checkUpdateActionFire$.pipe(take(1)).subscribe((flag: boolean) => {
          flag &&
            this.CustomFacade.updateStorageProcessActions(params.data, 'edit', null, nodeId, colId);
        });
        return valueChanged;
      },
    });

    this.innedGridActionAccess(fromEdit) &&
      colDef.push({
        ...defaultColumn,
        colId: 'actions',
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        maxWidth: grid_widths_map.sm_column_xs,
        cellRenderer: 'inlineActionButtonComponent',
        cellRendererParams: {
          showInlineEdit: true,
          showInlineCopy: true,
          showInlineDelete: true,
          copyDataEditRendarar: (data: any) => {
            this.CustomFacade.copyStorageProcessActions(data, 'copy');
          },
          copyData: (data: any) => {
            return false;
          },
          from: 'customStorage',
          inlineEditData: (data: any) => {
            enableEditing = data.data.enableEditing;
            resetData = data.data.resetData;
          },
          deleteInlineGrid: (data: any) => {
            this.alertConfirmation(data);
          },
        },
        lockVisible: true,
        autoHeaderHeight: true,
        lockPosition: right,
        resizable: false,
        pinned: right,
      });
    return colDef;
  }

  alertConfirmation(data: any) {
    this.swalService.confirm('Are you sure?', DELETE_RECORD_LABEL).then(confirmed => {
      if (confirmed) {
        this.CustomFacade.deleteStorageProcessActions(data, 'delete');
      }
    });
  }

  isEmpty(val: any) {
    if (val !== undefined || val !== null || val !== '') {
      val = val.trim();
    }
    return val === undefined || val === null || val === '';
  }
}
